import type { ContainerProps } from "@mui/material";
import { Box, Container, Divider, Paper } from "@mui/material";
import type { PropsWithChildren, ReactNode } from "react";
import LoaderBox from "@entrilia/shared/components/LoaderBox";
import PageHeader from "../../common/PageHeader";
import useSettingsContext from "../useSettingsContext";

interface Props {
  title: string;
  subtitle?: string;
  backButtonLink?: string;
  onBackButtonClick?: () => void;
  scrollOwner: "wrapper" | "children";
  maxWidth?: ContainerProps["maxWidth"];
  HeaderAction?: ReactNode;
  Subheader?: ReactNode;
  Footer?: ReactNode;
}

const SettingsWrapper = ({
  title,
  subtitle,
  backButtonLink,
  onBackButtonClick,
  scrollOwner,
  maxWidth = "md",
  HeaderAction,
  Subheader,
  Footer,
  children,
}: PropsWithChildren<Props>) => {
  const { loading } = useSettingsContext();
  const shouldWrapperScroll = scrollOwner === "wrapper";

  return (
    <LoaderBox loading={loading}>
      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Paper elevation={0} sx={{ flexShrink: 0, zIndex: 1050 }}>
          <PageHeader
            title={title}
            subtitle={subtitle}
            backButtonLink={backButtonLink}
            onBackButtonClick={onBackButtonClick}
          >
            {HeaderAction}
          </PageHeader>
        </Paper>

        {Subheader && (
          <Box sx={{ flexShrink: 0 }}>
            {Subheader}
            <Divider />
          </Box>
        )}

        <Box
          sx={{
            py: 2.5,
            flex: 1,
            minHeight: 0,
            boxSizing: "border-box",
            overflowY: shouldWrapperScroll ? "auto" : "hidden",
          }}
        >
          <Container
            maxWidth={maxWidth}
            sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
          >
            {children}
          </Container>
        </Box>

        {Footer && (
          <Paper elevation={0} sx={{ flexShrink: 0, zIndex: 1050 }}>
            <Divider />
            <Box sx={{ width: "100%" }}>{Footer}</Box>
          </Paper>
        )}
      </Box>
    </LoaderBox>
  );
};

export default SettingsWrapper;
